/* eslint-disable react/prop-types */
/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import styles from '../../../../styles';
import Button from '../../../../components/Button';
import Localized from '../../../../../shared/components/Localized';
import { StatusIcon } from '../../../../components/SVGIcon';
import { Checkbox } from '../../../../components/Form';

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;

  justify-content: center;

  @media (min-width: 48rem) {
    margin: 1.25rem auto;
    min-height: 9.3125rem;
    width: 37rem;
    border: 0.0625rem solid #d8dde6;
    margin: 1.25rem auto;
  }
`;

const Row = styled.div`
  display: flex;
  align-self: center;

  flex-wrap: wrap;
  width: 100%;

  @media (min-width: 768px) {
    &:not(:last-child) {
      border-bottom: 1px solid #d8dde6;
    }
  }
`;

const AllHeader = styled.div`
  width: 100%;
  ${styles.fonts.headingLabelCaps}
  background-color: white;
  border-top: 1px solid #d8dde6;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  display: flex;
  justify-content: space-between;

  @media (min-width: 768px) {
    width: 25%;
    background-color: #f4f6f9;
    border-right: 1px solid #d8dde6;
    border-top: 0;
    justify-content: flex-start;
  }
`;

const SeverityHeader = styled.div`
  ${styles.fonts.headingLabel}
  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    width: 25%;
    text-align: center;
    padding: 0.5rem 0;
  }
`;

const ServiceHeader = styled.div`
  width: 100%;
  ${styles.fonts.headingLabelCaps}
  background-color: #f4f6f9;
  border-top: 1px solid #d8dde6;
  border-bottom: 1px solid #d8dde6;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  display: flex;
  justify-content: space-between;

  @media (min-width: 768px) {
    width: 25%;
    border-right: 1px solid #d8dde6;
    border-top: 0;
    border-bottom: 0;
    justify-content: flex-start;
  }
`;
const ModelDiv = styled.div`
  height: 29.5625rem;

  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* make scrollbar transparent */
  }
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 18rem;
    margin-right: 1rem;
    margin-left: 1rem;
    height: 26.0625rem;
  }
`;
const ModelHeader = styled.div`
  display: flex;
  flex-direction: column;
  height: 76px;
  border-radius: 4px;
  box-shadow: 0 2px 0 0 #f0f2f7;
  background-color: #ffffff;
  justify-content: center;
  align-items: center;
  @media (min-width: 320px) and (max-width: 480px) {
    width: 320px;
    height: 66px;
  }
`;
const ModelHeading = styled.p`
  margin-top: 1rem;
  font-family: 'Salesforce Sans';
  font-size: 1.25rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #2b2826;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 20rem;
    height: 4.125rem;
    font-size: 1.125rem;
    text-align: center;
  }
`;
const ModelSubHeader = styled.p`
  font-family: 'Salesforce Sans';
  font-size: 0.75rem;
  padding-bottom: 1rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  margin-top: 0.5rem;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: center;
  color: #706e6b;
`;
const OrderedCheckbox = styled(Checkbox)`
  order: 2;

  @media (min-width: 48rem) {
    order: 1;
  }
`;

const OrderedLocalized = styled(Localized)`
  order: 1;

  @media (min-width: 48rem) {
    order: 2;
  }
`;

const OrderedHeader = styled.span`
  order: 1;

  @media (min-width: 48rem) {
    order: 2;
  }
`;
const NotificationType = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
`;

const NotificationToggle = styled.div`
  width: 7.125rem;
  height: 2rem;
  border-radius: 0.25rem;
  background-color: #ffffff;
`;

const NotificationTypeEmail = styled(Button)`
  width: 3.5625rem;
  height: 1.875rem;
  border-radius: 0.1875rem 0rem 0rem 0.1875rem;
  text-align: center;
  padding-left: 0;
  padding-right: 0;
  margin: 0 auto;
  &[disabled] {
    border: 1px solid #dddbda;
    background-color: #fff;
  }
`;

const NotificationTypeSMS = styled(Button)`
  width: 3.5625rem;
  height: 1.875rem;
  border-radius: 0rem 0.1875rem 0.1875rem 0rem;
  text-align: center;
  padding-left: 0;
  padding-right: 0;
  margin: 0 auto;
  &[disabled] {
    border: 1px solid #dddbda;
    background-color: #fff;
  }
`;
const ServiceCheckboxCell = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  @media (min-width: 48rem) {
    justify-content: space-around;
    width: 25%;
  }
`;

const StatusIconWithMargin = styled(StatusIcon)`
  margin-right: 0.5rem;
`;

const StatusIconSmallOnly = styled(StatusIcon)`
  @media (min-width: 48rem) {
    display: none;
  }
`;
const ModalIntro = styled.p`
  margin-left: 6.1875rem;
  margin-top: 2.125rem;
  color: rgb(112, 110, 107);
  font-family: Salesforce Sans;
  font-size: 0.75rem;
  font-weight: normal;
  height: 0.9375rem;
  letter-spacing: 0rem;
  margin-bottom: 1rem;
`;

const severities = [
  {
    key: 'major',
    localizationKey: 'disruption',
    imgUrl: '/assets/icons/icon-major.png',
  },
  {
    key: 'minor',
    localizationKey: 'degradation',
    imgUrl: '/assets/icons/icon-minor.png',
  },
  {
    key: 'maintenance',
    localizationKey: 'maintenance',
    imgUrl: '/assets/icons/icon-maintenance.png',
  },
];

export default class ServiceSeverityGrid extends React.Component {
  static propTypes = {
    instanceSubscription: PropTypes.object,
    severities: PropTypes.array,
    services: PropTypes.array,
    clickAllServices: PropTypes.func,
    clickService: PropTypes.func,
    clickServiceSeverity: PropTypes.func,
    readOnly: PropTypes.bool,
    onClose: PropTypes.func,
    invalid: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      subType: this.props.emailCheckBox ? 'email' : 'sms',
    };
    this.changeNotificationTypeSMS = this.changeNotificationTypeSMS.bind(this);
    this.changeNotificationTypeEmail = this.changeNotificationTypeEmail.bind(this);
    this.submit = this.submit.bind(this);
    this.addEmailSubscription = this.addEmailSubscription.bind(this);
    this.addSmsSubscription = this.addSmsSubscription.bind(this);
  }

  addEmailSubscription() {
    const { serviceSeverityGrid } = this.props;
    const emailAllNotifications = _.every(serviceSeverityGrid.state.services, s => {
      return s.major && s.minor && s.maintenance;
    });
    const body = {
      instanceKey: this.props.instanceKey,
      type: 'INSTANCE',
      subType: 'email',
      emailAddress: this.props.requests.subscriber.value.emailAddress,
      id: this.props.requests.subscriber.value.id,
    };

    if (emailAllNotifications) {
      body.allNotifications = true;
    } else {
      body.allNotifications = false;
      if (_.some(serviceSeverityGrid.state.services, s => s.major || s.minor || s.maintenance)) {
        body.ServiceSubscriptions = serviceSeverityGrid.state.services;
      } else {
        body.ServiceSubscriptions = [];
      }
    }

    return this.props.requests.postSubscription.handleRequest({ body });
  }

  addSmsSubscription() {
    const { serviceSeverityGrid } = this.props;
    const smsAllNotifications = _.every(serviceSeverityGrid.state.servicesSMS, s => {
      return s.major && s.minor && s.maintenance;
    });
    const body = {
      instanceKey: this.props.instanceKey,
      type: 'INSTANCE',
      subType: 'sms',
      emailAddress: this.props.requests.subscriber.value.emailAddress,
      id: this.props.requests.subscriber.value.id,
    };

    if (smsAllNotifications) {
      body.allNotifications = true;
    } else {
      body.allNotifications = false;
      if (_.some(serviceSeverityGrid.state.servicesSMS, s => s.major || s.minor || s.maintenance)) {
        body.ServiceSubscriptions = serviceSeverityGrid.state.servicesSMS;
      } else {
        body.ServiceSubscriptions = [];
      }
    }
    return this.props.requests.postSubscription.handleRequest({ body });
  }

  submit() {
    const { emailCheckBox, smsCheckBox } = this.props;
    if (emailCheckBox || smsCheckBox) {
      Promise.all([this.addEmailSubscription(), this.addSmsSubscription()])
        .then(() => {
          this.props.requests.subscriber.handleRequest();
          this.props.changeModal('success');
        })
        .catch(() => {
          this.props.requests.subscriber.handleRequest();
        });
    }
  }

  changeNotificationTypeSMS() {
    this.setState({ subType: 'sms' });
  }
  changeNotificationTypeEmail() {
    this.setState({ subType: 'email' });
  }
  componentDidMount() {
    const { emailCheckBox, smsCheckBox } = this.props;
    this.props.serviceSeverityGrid.actions.load({
      email: this.props.requests.instanceInfo.value.Services.map(service => {
        return {
          serviceKey: service.key,
          major: emailCheckBox,
          minor: emailCheckBox,
          maintenance: emailCheckBox,
        };
      }),
      sms: this.props.requests.instanceInfo.value.Services.map(service => {
        return {
          serviceKey: service.key,
          major: smsCheckBox,
          minor: smsCheckBox,
          maintenance: smsCheckBox,
        };
      }),
    });
  }
  render() {
    const {
      serviceSeverityGrid: {
        state,
        actions: { toggleAll, toggleService, toggleServiceSeverity },
      },
    } = this.props;

    return (
      <div>
        <ModelDiv>
          <ModelHeader>
            <ModelHeading>
              <Localized modelName="signupModal" modelKey="headerModal" modelAttribute="value" />
            </ModelHeading>
            <ModelSubHeader>
              <Localized modelName={'instance'} modelKey={this.props.instanceKey} modelAttribute={'label'} />
            </ModelSubHeader>
          </ModelHeader>
          <ModalIntro>I would like to receive notifications via:</ModalIntro>
          <NotificationType>
            <NotificationToggle>
              <NotificationTypeEmail
                disabled={!this.props.emailCheckBox}
                theme={this.state.subType === 'email' ? 'brand' : 'neutral'}
                onClick={this.changeNotificationTypeEmail}
                isActive={this.state.subType}
              >
                Email
              </NotificationTypeEmail>
              <NotificationTypeSMS
                disabled={!this.props.smsCheckBox}
                theme={this.state.subType === 'sms' ? 'brand' : 'neutral'}
                onClick={this.changeNotificationTypeSMS}
                isActive={this.state.subType}
              >
                SMS
              </NotificationTypeSMS>
            </NotificationToggle>
          </NotificationType>

          <Grid className={this.props.className}>
            <Row>
              <AllHeader>
                {this.state.subType === 'email' && (
                  <OrderedCheckbox
                    name={'allCorner'}
                    checked={state.allCheckedEmail}
                    indeterminate={state.allIndeterminate}
                    value={state.allCheckedEmail}
                    onChange={value => {
                      toggleAll(value, this.state.subType);
                    }}
                  />
                )}
                {this.state.subType === 'sms' && (
                  <OrderedCheckbox
                    name={'allCorner'}
                    checked={state.allCheckedSMS}
                    indeterminate={state.allIndeterminate}
                    value={state.allCheckedSMS}
                    onChange={value => {
                      toggleAll(value, this.state.subType);
                    }}
                  />
                )}
                <OrderedHeader>All Services</OrderedHeader>
              </AllHeader>
              {severities.map((severity, i) => {
                return (
                  <SeverityHeader key={i}>
                    <StatusIconWithMargin size="1rem" severity={severity.key} />
                    <Localized modelName={'statusKey'} modelKey={severity.localizationKey} modelAttribute={'label'} />
                  </SeverityHeader>
                );
              })}
            </Row>
            {this.state.subType === 'email' &&
              state.services.map((service, i) => {
                return (
                  <Row key={i}>
                    <ServiceHeader>
                      <OrderedCheckbox
                        name={`${service.serviceKey}`}
                        checked={service.checked}
                        indeterminate={service.indeterminate}
                        value={service.checked}
                        onChange={value => {
                          return toggleService(service.serviceKey, value, 'email');
                        }}
                      />
                      <OrderedLocalized modelName="service" modelKey={service.serviceKey} modelAttribute="label" />
                    </ServiceHeader>
                    {severities.map((severity, ii) => {
                      return (
                        <ServiceCheckboxCell key={i + '_' + ii}>
                          <StatusIconSmallOnly size="1rem" severity={severity.key} />
                          <Checkbox
                            name={`${service.serviceKey}-${severity.key}`}
                            id={`${service.serviceKey}-${severity.key}`}
                            checked={service[severity.key]}
                            value={service[severity.key]}
                            onChange={value => {
                              return toggleServiceSeverity(service.serviceKey, severity.key, value, this.state.subType);
                            }}
                          />
                        </ServiceCheckboxCell>
                      );
                    })}
                  </Row>
                );
              })}

            {this.state.subType === 'sms' &&
              state.servicesSMS.map((service, i) => {
                return (
                  <Row key={i}>
                    <ServiceHeader>
                      <OrderedCheckbox
                        name={`${service.serviceKey}`}
                        checked={service.checked}
                        indeterminate={service.indeterminate}
                        value={service.checked}
                        onChange={value => {
                          return toggleService(service.serviceKey, value, 'sms');
                        }}
                      />
                      <OrderedLocalized modelName="service" modelKey={service.serviceKey} modelAttribute="label" />
                    </ServiceHeader>
                    {severities.map((severity, ii) => {
                      return (
                        <ServiceCheckboxCell key={i + '_' + ii}>
                          <StatusIconSmallOnly size="1rem" severity={severity.key} />
                          <Checkbox
                            name={`${service.serviceKey}-${severity.key}`}
                            id={`${service.serviceKey}-${severity.key}`}
                            checked={service[severity.key]}
                            value={service[severity.key]}
                            onChange={value => {
                              return toggleServiceSeverity(service.serviceKey, severity.key, value, this.state.subType);
                            }}
                          />
                        </ServiceCheckboxCell>
                      );
                    })}
                  </Row>
                );
              })}
          </Grid>
        </ModelDiv>
        <div className="slds-modal__footer">
          <div className="slds-x-small-buttons--horizontal">
            <button className="slds-button slds-button--neutral" onClick={this.props.previousModal}>
              Back
            </button>
            <button className="slds-button slds-button--brand" onClick={this.submit}>
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }
}
